import { PropertySet } from '@vsolv/packages/properties/domain';
import { ClaimLifecycleStep } from '../claim-lifecycle-steps';
import { Coverage } from '../coverage';
import { CancelationRule } from './cancelation-rule';
import { CheckoutPlanDisplay } from './checkout-plan-display';
import { Document } from './document';
import { Email } from './email';
import { Plan } from './plan';
import { PolicyTerm } from './policy-term';
import { Status } from './status';

export interface CustomerPortalConfig {
  selfServeClaims: boolean;
  claimsPlaceholder: string | null;
  claimsPlaceholderWhenClaims?: string | null;
}

export interface Model {
  readonly id: string;
  referenceId: string | null;

  readonly created: Date;
  readonly modified: Date;
  deleted: Date | null;
  publishedDate: Date | null;

  /**
   * <tenant_prefix>-<year><random_numbers>
   *
   * HGP-20221234
   */
  readonly policyNumber: string;

  succeededBy?: Model | null;
  succeededById?: string | null;

  title: string;
  friendlyTitle?: string;
  icon: string;
  tagline: string;
  description?: string;
  carrier?: string;
  tags: string[];
  validity: string;
  welcomeEmailDelayHours: number;

  status: Status;

  propertySetId: string;
  propertySet?: PropertySet.Model;
  claimWaitingPeriod: number | null;

  plans?: Plan.Model[];

  coverages?: Coverage.Model[];

  claimLifecycleSteps?: ClaimLifecycleStep.Model[];

  emails?: Email[];
  cancelationRules?: CancelationRule[];

  termsAndConditionsId: string | null;
  termsAndConditions?: Document;

  coverageSummaryId: string | null;
  coverageSummary?: Document;

  terms: PolicyTerm[];

  requiresWarrantyActivation: boolean;

  /**
   * Controls whether internal sellers (Admin Dashboard users) can add a markup to the
   * warranty price at checkout. Disabled by default — policies opt in.
   */
  allowMarkup: boolean;

  /** Claim jobs are checked against the contract's eligibility rules before approval. Off by default. */
  checkClaimEligibility: boolean;

  /**
   * Controls the order of plan/term selection in checkout.
   * `TERM_FIRST` (default) — terms shown on top, plan cards below.
   * `PLAN_FIRST` — plans shown on top as a selector, terms shown below.
   */
  checkoutPlanDisplay: CheckoutPlanDisplay;

  customerPortalConfig: CustomerPortalConfig;

  order: number;
}

/**
 * Whether checkout markup is offered for a policy. Fail-open: markup is allowed unless the policy
 * explicitly disables it (`undefined`/`null` → allowed), preserving the legacy always-on behavior
 * for policies created before the `allowMarkup` flag existed. Single source of truth shared by the
 * checkout UI gate and the server-side provisioning guard.
 */
export function isMarkupAllowed(policy?: { allowMarkup?: boolean | null } | null): boolean {
  return policy?.allowMarkup !== false;
}

/**
 * The markup to actually charge at checkout: the provided value when the policy allows markup,
 * otherwise 0. Used by both the preview (subtotal) and confirmation (persisted) paths so the flag
 * is enforced wherever markup touches money, not just at the input.
 */
export function effectiveMarkup(
  policy: { allowMarkup?: boolean | null } | null | undefined,
  markup: number | null | undefined
): number {
  return isMarkupAllowed(policy) ? (markup ?? 0) : 0;
}
